import React, { useState } from "react";
import { Button, Alert, Badge } from "reactstrap";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useAuthUser from "../../../components/Hooks/useAuthUser";
import {
  checkEvery8DStatus,
  connectEvery8D,
  disconnectEvery8D,
} from "../api/every8dApi";
import Every8DConnectionModal from "./Every8DConnectionModal";
import { getUserRole } from "../../Users/api/usersApi";
import IntegrationCardLayout from "./IntegrationCardLayout";
import ConfirmActionModal from "../../../components/Common/ConfirmActionModal";

const Every8DIntegrationCard = () => {
  const { t } = useTranslation();
  const { tenantProfile } = useAuthUser();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [currentUserRole] = useState(() => getUserRole());

  const isTenantUser = currentUserRole === "TENANT_USER";
  const canManageIntegrations = !isTenantUser;

  // Query Every8D connection status
  const {
    data: every8dStatus,
    isLoading: isCheckingConnection,
    refetch: refetchConnection,
  } = useQuery({
    queryKey: ["every8d-status", tenantProfile?.handle],
    queryFn: () => checkEvery8DStatus(tenantProfile.handle),
    enabled: !!tenantProfile?.handle,
    retry: 2,
    staleTime: 5000,
  });

  // Get connection status from API response
  const isConnected = every8dStatus?.connected || false;
  const connectedBy = every8dStatus?.connectedBy;

  const connectionDetails = {
    connectAs: every8dStatus?.account || "-",
  };

  // Handle opening connection modal
  const handleOpenConnectionModal = () => {
    setShowConnectionModal(true);
    setConnectionError(null);
  };

  // Handle closing connection modal
  const handleCloseConnectionModal = () => {
    setShowConnectionModal(false);
    // Invalidate and refetch connection status after modal closes
    queryClient.invalidateQueries(["every8d-status", tenantProfile?.handle]);
    refetchConnection();
  };

  // Handle connect to Every8D
  const handleConnectToEvery8D = async (credentials) => {
    if (!tenantProfile.handle) {
      throw new Error(t("integration.errors.tenantMissing"));
    }

    await connectEvery8D(tenantProfile.handle, credentials);
  };

  // Handle disconnect from Every8D
  const handleDisconnectFromEvery8D = async () => {
    if (!tenantProfile.handle) {
      setConnectionError(t("integration.errors.tenantMissing"));
      return;
    }

    setIsLoading(true);
    setConnectionError(null);

    try {
      await disconnectEvery8D(tenantProfile.handle);
      // Refetch connection status after disconnect
      await refetchConnection();
    } catch (error) {
      console.error("Failed to disconnect from Every8D:", error);
      setConnectionError(t("integration.every8d.disconnectFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (isCheckingConnection) {
      return (
        <Badge className="integration-badge-disconnected">
          <span className="badge-dot"></span>
          {t("integration.status.checking")}
        </Badge>
      );
    }

    if (isConnected) {
      return (
        <Badge className="integration-badge-connected">
          <span className="badge-dot"></span>
          {t("integration.status.connected")}
        </Badge>
      );
    }

    return (
      <Badge className="integration-badge-disconnected">
        <span className="badge-dot"></span>
        {t("integration.status.notConnected")}
      </Badge>
    );
  };

  return (
    <>
      {/* Connection Modal */}
      <Every8DConnectionModal
        show={showConnectionModal}
        onClose={handleCloseConnectionModal}
        onConnect={handleConnectToEvery8D}
      />

      {/* Disconnecting stops SMS from every workflow, so ask first */}
      <ConfirmActionModal
        show={showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(false)}
        onConfirm={async () => {
          setShowDisconnectConfirm(false);
          await handleDisconnectFromEvery8D();
        }}
        title={t("integration.every8d.confirmDisconnect.title")}
        message={t("integration.every8d.confirmDisconnect.message")}
        confirmText={t("integration.actions.disconnect")}
        confirmColor="danger"
      />

      <IntegrationCardLayout
        logo={require("../../../assets/images/every8d-logo.png")}
        logoAlt={t("integration.every8d.logoAlt")}
        badge={getStatusBadge()}
        action={
          isConnected ? (
            <Button
              outline
              color="danger"
              className="btn-card-action"
              onClick={() => setShowDisconnectConfirm(true)}
              disabled={isLoading || !canManageIntegrations}
              title={
                !canManageIntegrations
                  ? t("integration.permissions.noDisconnect")
                  : ""
              }
            >
              {isLoading
                ? t("integration.actions.disconnecting")
                : t("integration.actions.disconnect")}
            </Button>
          ) : (
            <Button
              color="primary"
              className="btn-card-action"
              onClick={handleOpenConnectionModal}
              disabled={
                isLoading || !tenantProfile?.handle || !canManageIntegrations
              }
              title={
                !canManageIntegrations
                  ? t("integration.permissions.noConnect")
                  : ""
              }
            >
              {isLoading
                ? t("integration.actions.connecting")
                : t("integration.actions.connect")}
            </Button>
          )
        }
        description={t("integration.every8d.description")}
        fields={[
          [
            t("integration.details.every8dAccount"),
            isConnected ? connectionDetails.connectAs : "-",
          ],
          [
            t("integration.details.connectedBy"),
            isConnected && connectedBy ? connectedBy : "-",
          ],
        ]}
        error={
          connectionError && (
            <Alert color="danger" className="mt-3 mb-0">
              {connectionError}
            </Alert>
          )
        }
      />
    </>
  );
};

export default Every8DIntegrationCard;
